<template>
  <div class="main row">
    <div class="col-md-12">
      <KTCodePreview v-bind:title="'Chi tiết ngân sách năm ' + yearLabel">
        <template v-slot:toolbar>
          <b-button
            v-if="canEdit && checkPermission('BUDGET_UPDATE')"
            variant="primary"
            size="sm"
            class="mr-2"
            @click="openEditModal"
          >
            <i class="fas fa-edit"></i> Chỉnh sửa
          </b-button>
          <b-button
            v-if="parent.canApprove && checkPermission('BUDGET_UPDATE')"
            variant="success"
            size="sm"
            class="mr-2"
            @click="approveAction"
          >
            <i class="fas fa-check"></i> Duyệt
          </b-button>
          <b-button
            v-if="parent.canApprove && checkPermission('BUDGET_UPDATE')"
            variant="danger"
            size="sm"
            class="mr-2"
            @click="showRejectModal"
          >
            <i class="fas fa-times"></i> Không duyệt
          </b-button>
          <b-button variant="secondary" size="sm" @click="showHistoryModal">
            <i class="flaticon2-time"></i>&nbsp; Lịch sử
          </b-button>
        </template>

        <template v-slot:preview>
          <b-row class="mb-4">
            <b-col cols="3">
              <small class="text-muted">Năm</small>
              <div>
                <strong>{{ yearLabel }}</strong>
              </div>
            </b-col>
            <b-col cols="3">
              <small class="text-muted">Trạng thái</small>
              <div>
                <b-badge :variant="statusVariant(parent.status)">
                  {{ statusName(parent.status) }}
                </b-badge>
              </div>
            </b-col>
            <b-col cols="3">
              <small class="text-muted">Phòng ban</small>
              <div>{{ parent.departmentName || parent.departmentId }}</div>
            </b-col>
            <b-col cols="3">
              <small class="text-muted">Cửa hàng</small>
              <div>
                {{
                  parent.storeName ||
                  (parent.storeId ? parent.storeId : 'Cấp phòng ban')
                }}
              </div>
            </b-col>
          </b-row>

          <b-row class="mb-4">
            <b-col cols="6">
              <small class="text-muted">Doanh nghiệp</small>
              <div>{{ parent.companyName || parent.companyId }}</div>
            </b-col>
            <b-col cols="6">
              <small class="text-muted">Khoản mục phí</small>
              <div>
                {{ parent.costItem ? parent.costItem.name : parent.costItemId }}
              </div>
            </b-col>
          </b-row>

          <b-row class="mb-4">
            <b-col cols="4">
              <PurchaseRequestApprovalSummary
                :approvers="parent.approvers || []"
              />
            </b-col>
          </b-row>

          <b-row class="mt-4">
            <b-col cols="4">
              <div class="bl-metric-card">
                <div class="text-muted bl-metric-label">Tổng phân bổ</div>
                <div class="bl-metric-big">
                  {{ formatCompactMoney(summary.allocatedAmount) }}
                </div>
                <div class="text-muted bl-metric-small">
                  {{ convertPrice(summary.allocatedAmount) }} đ
                </div>
              </div>
            </b-col>
            <b-col cols="4">
              <div class="bl-metric-card">
                <div class="text-muted bl-metric-label">Tổng đã sử dụng</div>
                <div class="bl-metric-big">
                  {{ formatCompactMoney(summary.usedAmount) }}
                </div>
                <div class="text-muted bl-metric-small">
                  {{ budgetUsedPercent }}% phân bổ
                </div>
              </div>
            </b-col>
            <b-col cols="4">
              <div class="bl-metric-card bl-metric-card--remaining">
                <div class="text-muted bl-metric-label">Tổng còn lại</div>
                <div
                  class="bl-metric-big"
                  :class="
                    summary.remainingAmount < 0 ? 'text-danger' : 'text-success'
                  "
                >
                  {{ formatCompactMoney(summary.remainingAmount) }}
                </div>
                <div class="text-muted bl-metric-small">
                  {{ convertPrice(summary.remainingAmount) }} đ
                </div>
              </div>
            </b-col>
          </b-row>

          <div class="mt-5">
            <div class="font-weight-bold mb-3">Chi tiết 12 tháng</div>
            <b-table :items="months" :fields="monthFields" bordered hover small>
              <template #cell(period)="data">
                {{ formatMonth(data.value) }}
              </template>
              <template #cell(allocatedAmount)="data">
                {{ convertPrice(data.value) }}
              </template>
              <template #cell(usedAmount)="data">
                {{ convertPrice(data.value) }}
              </template>
              <template #cell(remainingAmount)="data">
                <span
                  :class="data.value < 0 ? 'text-danger font-weight-bold' : ''"
                >
                  {{ convertPrice(data.value) }}
                </span>
              </template>
              <template #cell(actions)="row">
                <b-button
                  size="sm"
                  variant="secondary"
                  @click="openMonthDetail(row.item)"
                >
                  Chi tiết
                </b-button>
              </template>
            </b-table>
          </div>

          <BudgetLineHistoryModal ref="historyModal" :histories="histories" />

          <b-button variant="secondary" @click="goBack" class="mt-3">
            <i class="fas fa-arrow-left"></i> Quay lại
          </b-button>

          <b-modal
            ref="rejectModal"
            title="Không duyệt ngân sách"
            @ok="confirmReject"
            ok-variant="danger"
            ok-title="Không duyệt"
            cancel-title="Đóng"
          >
            <b-form-group>
              <template #label>
                Lý do <span class="text-danger">*</span>
              </template>
              <b-form-textarea v-model="rejectForm.note" rows="3" required />
            </b-form-group>
          </b-modal>

          <b-modal
            ref="approveModal"
            title="Duyệt ngân sách"
            @ok="confirmApprove"
            ok-variant="success"
            ok-title="Duyệt"
            cancel-title="Đóng"
          >
            <b-form-group label="Ghi chú duyệt">
              <b-form-textarea
                v-model="approveForm.note"
                rows="3"
                placeholder="Nhập ghi chú (nếu có)"
              />
            </b-form-group>
          </b-modal>

          <b-modal
            ref="editModal"
            title="Chỉnh sửa ngân sách 12 tháng"
            size="xl"
            @ok="confirmEdit"
            ok-variant="primary"
            ok-title="Lưu"
            :ok-disabled="isEditing"
            cancel-title="Đóng"
          >
            <BudgetLineDeclareForm
              ref="editForm"
              :mode="'edit'"
              :disabled="isEditing"
              :showBack="false"
              :showSubmit="false"
              :submitText="'Lưu'"
              :initialValue="editInitialValue"
              @submit="submitEdit"
            />
          </b-modal>
        </template>
      </KTCodePreview>
    </div>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import { checkPermission } from '@/utils/saveDataToLocal';
import {
  makeToastSuccess,
  makeToastFaile,
  convertPrice,
  handleError,
} from '@/utils/common';
import moment from 'moment';
import BudgetLineHistoryModal from './components/BudgetLineHistoryModal.vue';
import PurchaseRequestApprovalSummary from '../purchase-requests/components/PurchaseRequestApprovalSummary.vue';
import {
  BUDGET_STATUS,
  BUDGET_STATUS_BADGE_VARIANT,
  WORKFLOW_APPROVAL_STEP_STATUS,
} from '@/utils/enum';
import { BUDGET_STATUS_NAME } from '@/utils/enum-name';
import BudgetLineDeclareForm from './components/BudgetLineDeclareForm.vue';

export default {
  name: 'BudgetYearGroupDetail',
  components: {
    KTCodePreview,
    PurchaseRequestApprovalSummary,
    BudgetLineHistoryModal,
    BudgetLineDeclareForm,
  },
  data() {
    return {
      parent: {},
      months: [],
      summary: { allocatedAmount: 0, usedAmount: 0, remainingAmount: 0 },
      histories: [],
      rejectForm: { note: '' },
      approveForm: { note: '' },
      isEditing: false,
      editInitialValue: {},
      monthFields: [
        { key: 'period', label: 'Tháng', thStyle: { width: '10%' } },
        { key: 'allocatedAmount', label: 'Phân bổ', tdClass: 'text-right' },
        { key: 'usedAmount', label: 'Đã dùng', tdClass: 'text-right' },
        { key: 'remainingAmount', label: 'Còn lại', tdClass: 'text-right' },
        { key: 'actions', label: '', thStyle: { width: '15%' }, tdClass: 'text-center' },
      ],
      WORKFLOW_APPROVAL_STEP_STATUS,
    };
  },
  computed: {
    yearLabel() {
      const p = this.parent?.period;
      if (!p) return '';
      return moment(p).format('YYYY');
    },
    canEdit() {
      return Number(this.parent?.status) === Number(BUDGET_STATUS.DRAFT);
    },
    budgetUsedPercent() {
      const allocated = Number(this.summary?.allocatedAmount);
      const used = Number(this.summary?.usedAmount);
      if (!allocated || allocated <= 0) return 0;
      const raw = (used / allocated) * 100;
      if (!Number.isFinite(raw)) return 0;
      return Math.max(0, Math.min(100, Math.round(raw)));
    },
  },
  created() {
    const id = this.$route.query.id;
    if (id) {
      this.fetchDetail(id);
      this.fetchHistory(id);
    }
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Kế toán', route: '#' },
      { title: 'Ngân sách', route: '/budget/budget-lines' },
      { title: 'Chi tiết năm' },
    ]);
  },
  methods: {
    checkPermission,
    makeToastSuccess,
    makeToastFaile,
    convertPrice,
    statusVariant(val) {
      return BUDGET_STATUS_BADGE_VARIANT[val] || 'secondary';
    },
    statusName(val) {
      return BUDGET_STATUS_NAME[val] || '';
    },
    formatMonth(val) {
      return val ? moment(val).format('MM/YYYY') : '';
    },
    formatCompactMoney(val) {
      const n = Number(val);
      if (!Number.isFinite(n)) return '--';
      const abs = Math.abs(n);
      if (abs >= 1e9) {
        const v = n / 1e9;
        return `${this.trimCompactDecimals(v)}B`;
      }
      if (abs >= 1e6) {
        const v = n / 1e6;
        return `${this.trimCompactDecimals(v)}M`;
      }
      if (abs >= 1e3) {
        const v = n / 1e3;
        return `${this.trimCompactDecimals(v)}K`;
      }
      return String(Math.round(n));
    },
    trimCompactDecimals(v) {
      const rounded = Math.round(v * 10) / 10;
      return Number.isInteger(rounded) ? String(rounded) : String(rounded);
    },
    fetchDetail(id) {
      ApiService.get(`v2/budget-lines/parents/${id}/detail`).then(
        ({ data }) => {
          if (data.status === 200) {
            this.parent = data.data.parent || {};
            this.months = data.data.months || [];
            this.summary = data.data.summary || {
              allocatedAmount: 0,
              usedAmount: 0,
              remainingAmount: 0,
            };
          }
        },
      );
    },
    fetchHistory(id) {
      ApiService.get(`v2/budget-lines/${id}/history`).then(({ data }) => {
        if (data.status === 200) {
          this.histories = data.data || [];
        }
      });
    },
    showHistoryModal() {
      this.$refs.historyModal?.open();
    },
    approveAction() {
      this.approveForm.note = '';
      this.$refs.approveModal.show();
    },
    confirmApprove(evt) {
      evt.preventDefault();
      const id = this.$route.query.id;
      ApiService.patch(`v2/budget-lines/change-status/${id}`, {
        status: WORKFLOW_APPROVAL_STEP_STATUS.APPROVED,
        note: this.approveForm.note || '',
      })
        .then(() => {
          this.makeToastSuccess('Cập nhật trạng thái duyệt thành công!');
          this.fetchDetail(id);
          this.fetchHistory(id);
          this.$refs.approveModal.hide();
        })
        .catch((err) => handleError(err));
    },
    showRejectModal() {
      this.rejectForm.note = '';
      this.$refs.rejectModal.show();
    },
    confirmReject(evt) {
      evt.preventDefault();
      const note = this.rejectForm.note || '';
      if (!note.trim()) {
        this.makeToastFaile('Vui lòng nhập lý do không duyệt!');
        return;
      }
      const id = this.$route.query.id;
      ApiService.patch(`v2/budget-lines/change-status/${id}`, {
        status: WORKFLOW_APPROVAL_STEP_STATUS.REJECTED,
        note,
      })
        .then(() => {
          this.makeToastSuccess('Cập nhật trạng thái duyệt thành công!');
          this.fetchDetail(id);
          this.fetchHistory(id);
          this.$refs.rejectModal.hide();
        })
        .catch((err) => handleError(err));
    },
    openMonthDetail(item) {
      this.$router.push({ name: 'detail-budget-line', query: { id: item.id } });
    },
    openEditModal() {
      const byMonth = new Array(12).fill(0);
      (this.months || []).forEach((m) => {
        const idx = m?.period ? Number(moment(m.period).format('M')) - 1 : -1;
        if (idx >= 0 && idx < 12) byMonth[idx] = Number(m.allocatedAmount) || 0;
      });
      this.editInitialValue = {
        year: this.yearLabel ? Number(this.yearLabel) : undefined,
        companyId: this.parent?.companyId ?? null,
        departmentId: this.parent?.departmentId ?? null,
        departmentName: this.parent?.departmentName || '',
        costItemId: this.parent?.costItemId ?? null,
        costItemName: this.parent?.costItem?.name || '',
        storeId: this.parent?.storeId ?? null,
        storeName: this.parent?.storeName || '',
        monthAmounts: byMonth,
        description:
          (this.histories &&
            this.histories[0] &&
            this.histories[0].description) ||
          '',
      };
      this.$refs.editModal.show();
    },
    confirmEdit(evt) {
      evt.preventDefault();
      this.$refs.editForm?.onSubmit?.();
    },
    submitEdit(payload) {
      const id = this.$route.query.id;
      this.isEditing = true;
      ApiService.put(`v2/budget-lines/parents/${id}`, payload)
        .then(() => {
          this.makeToastSuccess('Cập nhật ngân sách năm thành công!');
          this.fetchDetail(id);
          this.fetchHistory(id);
          this.$refs.editModal.hide();
        })
        .catch((err) => handleError(err))
        .finally(() => {
          this.isEditing = false;
        });
    },
    goBack() {
      const from = this.$route?.query?.from;
      if (from) {
        this.$router.push(String(from)).catch(() => {});
        return;
      }
      this.$router.push({ name: 'list-budget-lines' }).catch(() => {});
    },
  },
};
</script>

<style scoped>
.bl-metric-card {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 16px;
  min-height: 110px;
}

.bl-metric-card--remaining {
  background: #e9fbf3;
}

.bl-metric-label {
  font-size: 0.875rem;
}

.bl-metric-big {
  font-size: 1.6rem;
  font-weight: 900;
  line-height: 1.2;
  margin-top: 6px;
}

.bl-metric-small {
  font-size: 0.875rem;
  margin-top: 6px;
}
</style>

